import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { authenticationApi } from "pages/authentication/authenticationService";
import { AuthenticationResponse, AuthDeviceDetails, loginApi } from "pages/login/loginService";
import { mfaApi, MfaResponse } from "pages/MFA/mfaService";
import { RegisterResponse, registrationApi } from "pages/registration/registrationService";
import type { RootState } from "store";

interface MfaState {
  flowId: string;
  mfaDevices: AuthDeviceDetails[];
  mfaResponse: MfaResponse;
  resendCodeDeviceId: string;
  authDeviceType: string;
}

const initialState: MfaState = {
  flowId: "",
  mfaDevices: [],
  mfaResponse: {
    id: "",
  },
  resendCodeDeviceId: "",
  authDeviceType: "",
};

const mfaSlice = createSlice({
  name: "mfaState",
  initialState,
  reducers: {
    setResendCodeDeviceId: (state, action: PayloadAction<string>) => {
      state.resendCodeDeviceId = action.payload;
    },
    setAuthDeviceType: (state, action: PayloadAction<string>) => {
      state.authDeviceType = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(loginApi.endpoints.postLogin.matchFulfilled, (state, action: PayloadAction<AuthenticationResponse>) => {
      if ("_embedded" in action.payload) {
        state.flowId = action.payload.flowId;
        state.mfaDevices = action.payload._embedded?.devices ?? [];
      }
    });

    builder.addMatcher(registrationApi.endpoints.postRegistration.matchFulfilled, (state, action: PayloadAction<RegisterResponse>) => {
      if ("_embedded" in action.payload) {
        state.flowId = action.payload.flowId;
        state.mfaDevices = action.payload._embedded?.devices ?? [];
      }
    });

    builder.addMatcher(mfaApi.endpoints.postMfa.matchFulfilled, (state, action: PayloadAction<MfaResponse>) => {
      state.mfaResponse = action.payload;
    });

    builder.addMatcher(authenticationApi.endpoints.postOtpValidation.matchFulfilled, (state) => {
      state.flowId = "";
      state.mfaDevices = [];
      state.mfaResponse = { id: "" };
      state.resendCodeDeviceId = "";
    });
  },
});

export const getMfaFlowId = (state: RootState): string => state.mfaState.flowId;
export const getMfaDevices = (state: RootState): AuthDeviceDetails[] => state.mfaState.mfaDevices;
export const getAuthOtpDetails = (state: RootState): MfaResponse => state.mfaState.mfaResponse;
export const getResendCodeDeviceId = (state: RootState): string => state.mfaState.resendCodeDeviceId;
export const getAuthDeviceType = (state: RootState): string => state.mfaState.authDeviceType;

export const { setResendCodeDeviceId, setAuthDeviceType } = mfaSlice.actions;

export default mfaSlice.reducer;
